import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useDataProvider, useNotify, useQuery } from 'react-admin';
import { Field, FormSection, ToggleField } from '@src/components/Common/Form';
import { setActiveTeamLocalStorage, spinnerElement } from '@src/components/Common/utils';
import useGetGeneralSettings from '@src/providers/useGetGeneralSettings';

/** the fields that live under the team's preferences rather than on the team */
const PREFERENCE_FIELDS = ['allow_submit_submission_builder', 'allow_service_creation'];

/** what a name a declaration can be keyed by may not contain — the API's rule */
const NAME_FORBIDDEN_CHARS = [',', '/', '&', '#', ' ', '\\'];
const NAME_RESERVED = ['services', 'metadata'];

/**
 * How the team behaves — its notification address and what its members are
 * allowed to do from the GUI — in one form behind one button, the way a
 * service's settings are.
 *
 * The two switches are only worth asking about where the deployment has the
 * feature on at all: a toggle for something nobody can reach is a setting that
 * does nothing, so it is not rendered rather than rendered and disabled.
 */
const SettingsTab = ({
    teamId,
    /**
     * Read a team the caller is not a member of — platform admins only, and
     * only the read: a write is explicit by nature, so the API widens PATCH for
     * a platform admin without being asked.
     */
    allTeams = false,
}: { teamId: number; allTeams?: boolean }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    /* the values a rename is waiting on, kept until the sheet has finished
       closing so its contents do not blank out mid-animation */
    const [pending, setPending] = useState<any>(null);
    const [confirming, setConfirming] = useState(false);

    const generalSettings = useGetGeneralSettings();
    const builder = generalSettings.find((setting) => setting.name === 'SubmissionBuilder');
    const creator = generalSettings.find((setting) => setting.name === 'ServiceCreator');

    const showBuilder =
        builder?.config?.show_submission_builder === true && builder?.config?.enable_submit_functionality === true;
    const showCreator =
        creator?.config?.show_create_service_option === true && creator?.config?.allow_service_creation === true;

    const { data: team, loaded } = useQuery({
        type: 'getOne',
        resource: 'teams',
        payload: { id: teamId, ...(allTeams ? { filter: { all_teams: true } } : {}) },
    });

    useEffect(() => {
        if (!team) return;
        form.setFieldsValue({
            name: team.name,
            email: team.email,
            allow_submit_submission_builder: team.preferences?.allow_submit_submission_builder,
            allow_service_creation: team.preferences?.allow_service_creation,
        });
    }, [team, form]);

    /**
     * The API takes a PUT, so the preferences it is sent have to be the whole
     * object: anything this form does not show still travels with it, or it
     * falls back to the model default.
     */
    const submit = (values: any) => {
        setSaving(true);
        const renamed = team && values.name !== team.name;

        const preferences: Record<string, any> = { ...(team?.preferences || {}) };
        const payload: Record<string, any> = {};
        Object.entries(values).forEach(([key, value]) => {
            if (PREFERENCE_FIELDS.includes(key)) preferences[key] = value;
            else payload[key] = value;
        });

        return dataProvider
            .update('teams', { id: teamId, data: { ...payload, preferences }, previousData: { id: teamId } })
            .then((response: any) => {
                // the top bar reads the active team out of local storage, so the
                // name and address it shows have to be updated with the record
                setActiveTeamLocalStorage(response.data);
                notify('Team settings have been updated', 'success');
                /* the team's name is on the page in places this form does not
                   own — the header among them — and they read it as it was when
                   they rendered, so a rename is taken the way activating a
                   different team is taken: the page is loaded again under it */
                if (renamed) window.location.reload();
            })
            .catch((error: any) => {
                notify('Something went wrong', 'warning');
                if (error.status === 400 && error.body) {
                    Object.entries<any>(error.body).forEach(([name, errors]) => form.setFields([{ name, errors }]));
                }
            })
            .finally(() => setSaving(false));
    };

    /**
     * A rename is asked about before it is made. The team's name is the
     * top-level key of every declaration submitted for it, so the cost of the
     * change is paid somewhere this form cannot reach — in whatever repository
     * the declaration lives in — and the moment to say so is while the change
     * can still be called off.
     */
    const save = (values: any) => {
        if (!team || values.name === team.name) return submit(values);
        setPending(values);
        setConfirming(true);
        return undefined;
    };

    if (!loaded) return <div className="tm-tab">{spinnerElement}</div>;

    return (
        <div className="tm-tab">
            <div className="nf-form nf-form--sheet tm-settings">
                <Form form={form} onFinish={save} layout="vertical" colon={false}>
                    <FormSection id="team-settings" title="Settings" bare>
                        <Field
                            name="name"
                            label="Team name"
                            span={6}
                            rules={[
                                { required: true, message: 'A team has to have a name' },
                                {
                                    validator: (_rule: any, value: string) => {
                                        if (!value) return Promise.resolve();
                                        const bad = NAME_FORBIDDEN_CHARS.filter((char) => value.includes(char));
                                        if (bad.length)
                                            return Promise.reject(
                                                new Error(
                                                    `A name cannot contain ${bad
                                                        .map((char) => (char === ' ' ? 'a space' : char))
                                                        .join(', ')}`
                                                )
                                            );
                                        if (NAME_RESERVED.includes(value))
                                            return Promise.reject(new Error(`"${value}" is a reserved name`));
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input placeholder="team-name" />
                        </Field>

                        <Field
                            name="email"
                            label="Notification email"
                            span={6}
                            hint="Where a service with email notifications on writes when one of this team's change instances is raised."
                            rules={[{ type: 'email', message: 'That does not look like an email address' }]}
                        >
                            <Input placeholder="team@example.com" />
                        </Field>

                        {showBuilder && (
                            <ToggleField
                                name="allow_submit_submission_builder"
                                span={3}
                                label="Submit from the builder"
                                hint="Let members submit straight from the submission builder."
                            />
                        )}

                        {showCreator && (
                            <ToggleField
                                name="allow_service_creation"
                                span={3}
                                label="Create services in the GUI"
                                hint="Let members author a new service without going through a submission."
                            />
                        )}

                        <div className="tm-save">
                            <Button type="primary" htmlType="submit" loading={saving}>
                                Save changes
                            </Button>
                        </div>
                    </FormSection>
                </Form>
            </div>

            {/* The rename, as the declaration will read it: the name it is
                keyed by now, the name it would be keyed by, and the one line
                of the file that has to change. */}
            <Modal
                className="tm-rename"
                open={confirming}
                centered
                width={470}
                closable={false}
                maskClosable={!saving}
                afterClose={() => setPending(null)}
                onCancel={() => setConfirming(false)}
                title={
                    <span className="tm-rename__head">
                        <span className="tm-rename__ico" aria-hidden="true">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                        </span>
                        Rename this team?
                    </span>
                }
                footer={
                    <>
                        <Button onClick={() => setConfirming(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            loading={saving}
                            onClick={() => submit(pending).finally(() => setConfirming(false))}
                        >
                            Rename team
                        </Button>
                    </>
                }
            >
                <p className="tm-rename__body">
                    A declaration is keyed by the team name at its top level, so every submission made for this team has
                    to be re-keyed to match.
                </p>

                <div className="tm-rename__diff">
                    <div className="tm-rename__diff-bar">Declaration</div>
                    <div className="tm-rename__line tm-rename__line--out">
                        <span className="tm-rename__sign" aria-hidden="true">
                            -
                        </span>
                        {team?.name}:
                    </div>
                    <div className="tm-rename__line tm-rename__line--in">
                        <span className="tm-rename__sign" aria-hidden="true">
                            +
                        </span>
                        {pending?.name}:
                    </div>
                    <div className="tm-rename__line tm-rename__line--ctx">
                        <span className="tm-rename__sign" aria-hidden="true" />
                        {'  # everything under it is unchanged'}
                    </div>
                </div>

                <p className="tm-rename__warn">Until that key is changed, a submission for this team is rejected.</p>
            </Modal>
        </div>
    );
};

export default SettingsTab;
